import * as THREE from 'three';
import {
  MAX_QUEUE_LINE_SHOPPERS,
  MAX_QUEUE_LINES,
  queueLineColorMap,
  SELECTION_COLOR,
  SELECTION_OPACITY,
} from '../components/views/FebView/constants';
import {
  addSelectionOverlay,
  createShopperMesh,
  isShopperOverlapping,
  updateShopperBadgeSelection,
  updateShopperOrderBadge,
} from '../components/views/FebView/meshOperations';
import {
  isShopperMesh,
  QueueLineModel,
  ShopperMesh,
  ShopperType,
} from '../components/views/FebView/types';
import { IQueueLineManager } from './interfaces';

export class QueueLineManager implements IQueueLineManager {
  /**
   * Initialize queue lines with default configuration
   */
  public initializeQueueLines(): QueueLineModel[] {
    return Array.from({ length: MAX_QUEUE_LINES }, (_, index) => ({
      lineId: index + 1,
      color: queueLineColorMap[(index + 1) as keyof typeof queueLineColorMap],
      shoppers: [],
    }));
  }

  /**
   * Find the first available queue line for new shoppers
   */
  public getAvailableQueueLine(
    queueLines: QueueLineModel[]
  ): QueueLineModel | null {
    // Find the first empty queue line (with no shoppers)
    for (const line of queueLines) {
      if (line.shoppers.length === 0) {
        return line;
      }
    }
    return null;
  }

  /**
   * Add a shopper to a queue line
   */
  public addShopperToQueueLine(
    scene: THREE.Scene,
    position: THREE.Vector3,
    queueLine: QueueLineModel,
    shopperType: ShopperType
  ): ShopperMesh | null {
    if (queueLine.shoppers.length >= MAX_QUEUE_LINE_SHOPPERS) {
      return null;
    }

    // Create the new shopper
    const newOrderInLine = queueLine.shoppers.length + 1;
    const shopperMesh = createShopperMesh(
      position,
      shopperType,
      newOrderInLine,
      queueLine.lineId,
      queueLine.color,
      0, // default rotation
      scene // pass scene for badge container
    );

    // Add to scene
    scene.add(shopperMesh);

    return shopperMesh;
  }

  /**
   * Update shopper type by replacing the mesh
   */
  public updateShopperType(
    scene: THREE.Scene,
    shopper: ShopperMesh,
    newType: ShopperType
  ): void {
    if (!isShopperMesh(shopper)) {
      console.error('Invalid shopper mesh provided to updateShopperType');
      return;
    }

    // Store the shopper's position and properties
    const position = shopper.position.clone();
    const orderInLine = shopper.userData.orderInLine;
    const lineId = shopper.userData.lineId;
    const color = shopper.userData.color;
    const rotation = shopper.rotation.z;
    const badgeContainer = shopper.userData.badgeContainer;

    // Remove the old shopper from the scene
    scene.remove(shopper);
    if (badgeContainer) {
      scene.remove(badgeContainer);
    }

    // Create a new shopper with the updated type
    const newShopper = createShopperMesh(
      position,
      newType,
      orderInLine,
      lineId,
      color,
      rotation,
      scene
    );

    // Add the new shopper to the scene
    scene.add(newShopper);

    // If the shopper was selected, add the selection overlay
    if (shopper.userData.hasOverlay) {
      addSelectionOverlay(
        newShopper,
        scene,
        SELECTION_COLOR,
        SELECTION_OPACITY
      );
      updateShopperBadgeSelection(newShopper, true);
    }
  }

  /**
   * Update the elapsed wait time for a shopper
   */
  public updateShopperWaitTime(shopper: ShopperMesh, waitTime: string): void {
    shopper.userData.elapsedWaitTime = waitTime;
  }

  /**
   * Remove a shopper and resequence the queue line
   */
  public removeShopper(
    scene: THREE.Scene,
    shopper: ShopperMesh,
    queueLines: QueueLineModel[]
  ): boolean {
    console.log(
      'removeShopper called with shopper:',
      shopper.id,
      shopper.userData
    );

    // Remove the selection overlay if it exists
    if (shopper.userData.hasOverlay) {
      const overlayUuid = shopper.userData.hasOverlay;
      const overlay = scene.children.find((obj) => obj.uuid === overlayUuid);
      if (overlay) {
        scene.remove(overlay);
      }
    }

    // Remove the badge container from the scene if it exists
    if (shopper.userData.badgeContainer) {
      console.log('Removing badge container');
      scene.remove(shopper.userData.badgeContainer);
    }

    // Remove the shopper from the scene
    console.log('Removing shopper from scene');
    scene.remove(shopper);

    // Get all remaining shoppers in this queue line
    const remainingShoppers = scene.children
      .filter(
        (obj): obj is ShopperMesh =>
          obj instanceof THREE.Mesh &&
          obj.userData.isQueueLineShopper &&
          obj.userData.lineId === shopper.userData.lineId
      )
      .sort((a, b) => a.userData.orderInLine - b.userData.orderInLine);

    console.log('Remaining shoppers:', remainingShoppers.length);

    // Resequence the remaining shoppers
    remainingShoppers.forEach((shopperMesh, index) => {
      const newOrderInLine = index + 1;
      if (shopperMesh.userData.orderInLine !== newOrderInLine) {
        shopperMesh.userData.orderInLine = newOrderInLine;
        updateShopperOrderBadge(shopperMesh, newOrderInLine);
      }
    });

    // Update the queue line model if found
    const lineIndex = queueLines.findIndex(
      (line) => line.lineId === shopper.userData.lineId
    );
    if (lineIndex !== -1) {
      queueLines[lineIndex].shoppers = remainingShoppers;
    }

    // Check if all shoppers have been deleted
    const allDeleted = this.areAllShoppersDeleted(scene);
    console.log('All shoppers deleted:', allDeleted);

    return allDeleted;
  }

  /**
   * Check for overlapping shoppers in the scene
   */
  public checkForOverlappingShoppers(scene: THREE.Scene): string[] {
    const shoppers = scene.children.filter(
      (obj): obj is ShopperMesh =>
        obj instanceof THREE.Mesh && obj.userData.isQueueLineShopper === true
    );

    const allObjects = scene.children.filter(
      (obj) => obj instanceof THREE.Mesh && obj.userData.isInteractive === true
    );

    const overlappingShopperIds: string[] = [];

    shoppers.forEach((shopper) => {
      if (isShopperOverlapping(shopper, allObjects as THREE.Mesh[])) {
        overlappingShopperIds.push(shopper.id.toString());
      }
    });

    return overlappingShopperIds;
  }

  /**
   * Find the queue line a shopper belongs to
   */
  public getShopperQueueLine(
    queueLines: QueueLineModel[],
    shopper: ShopperMesh
  ): QueueLineModel | null {
    return (
      queueLines.find((line) => line.lineId === shopper.userData.lineId) || null
    );
  }

  /**
   * Check if all shoppers have been deleted from the scene
   */
  public areAllShoppersDeleted(scene: THREE.Scene): boolean {
    const shoppers = scene.children.filter(
      (obj) => obj instanceof THREE.Mesh && obj.userData.isQueueLineShopper
    );
    console.log('areAllShoppersDeleted - shoppers count:', shoppers.length);
    return shoppers.length === 0;
  }

  /**
   * Handle queue line mode transitions and state management
   */
  public toggleQueueLineMode(
    isCurrentlyInMode: boolean,
    queueLines: QueueLineModel[],
    currentQueueLine: QueueLineModel | null,
    callbacks: {
      setIsInQueueLineMode: (mode: boolean) => void;
      setCurrentQueueLine: (line: QueueLineModel | null) => void;
      setQueueLines: (lines: QueueLineModel[]) => void;
      clearSelections: () => void;
      checkOverlaps: () => void;
    }
  ): void {
    const nextMode = !isCurrentlyInMode;

    if (nextMode) {
      // Entering queue line mode
      const availableQueueLine = this.getAvailableQueueLine(queueLines);
      callbacks.setCurrentQueueLine(availableQueueLine);
      callbacks.clearSelections();
    } else {
      // Exiting queue line mode
      callbacks.setCurrentQueueLine(null);

      // Update queueLines with the current queue line's shoppers
      if (currentQueueLine) {
        callbacks.setQueueLines(
          queueLines.map((line) =>
            line.lineId === currentQueueLine.lineId ? currentQueueLine : line
          )
        );
      }

      // Check for overlaps when exiting queue line mode
      callbacks.checkOverlaps();
    }

    callbacks.setIsInQueueLineMode(nextMode);
  }

  /**
   * Handle queue line mode click to add shoppers
   */
  public handleQueueLineModeClick(
    event: MouseEvent,
    mountRef: React.RefObject<HTMLDivElement>,
    refs: {
      scene: React.RefObject<THREE.Scene>;
      camera: React.RefObject<THREE.Camera>;
      raycaster: React.RefObject<THREE.Raycaster>;
      floorPlane: React.RefObject<THREE.Mesh>;
    },
    currentQueueLine: QueueLineModel,
    selectedShopperType: ShopperType,
    callbacks: {
      setCurrentQueueLine: (line: QueueLineModel) => void;
      setQueueLines: (
        updater: (lines: QueueLineModel[]) => QueueLineModel[]
      ) => void;
      checkOverlaps: () => void;
    }
  ): void {
    if (
      !refs.scene.current ||
      !refs.camera.current ||
      !refs.raycaster.current ||
      !refs.floorPlane.current ||
      !mountRef.current
    ) {
      return;
    }

    // Get click position in normalized device coordinates
    const rect = mountRef.current.getBoundingClientRect();
    const normalizedX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const normalizedY = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Raycasting to find intersection with floor
    refs.raycaster.current.setFromCamera(
      { x: normalizedX, y: normalizedY },
      refs.camera.current
    );

    const intersects = refs.raycaster.current.intersectObject(
      refs.floorPlane.current
    );
    if (intersects.length === 0) return;

    // Create shopper at intersection point
    const position = intersects[0].point;
    const shopperMesh = this.addShopperToQueueLine(
      refs.scene.current,
      position,
      currentQueueLine,
      selectedShopperType
    );

    if (shopperMesh) {
      // Update currentQueueLine with the new shopper
      callbacks.setCurrentQueueLine({
        ...currentQueueLine,
        shoppers: [...currentQueueLine.shoppers, shopperMesh],
      });

      // Update queue lines state
      callbacks.setQueueLines((prevLines) =>
        prevLines.map((line) =>
          line.lineId === currentQueueLine.lineId
            ? {
                ...line,
                shoppers: [...line.shoppers, shopperMesh],
              }
            : line
        )
      );

      // Check for overlapping shoppers
      callbacks.checkOverlaps();
    }
  }

  /**
   * Handle shopper updates (type, removal, wait time)
   */
  public handleShopperUpdate(
    scene: THREE.Scene,
    shopper: ShopperMesh,
    action: 'updateShopperType' | 'removeShopper' | 'updateElapsedWaitTime',
    value: ShopperType | string | undefined,
    callbacks: {
      setQueueLines: (
        updater: (lines: QueueLineModel[]) => QueueLineModel[]
      ) => void;
      setSelectedMeshes: (meshes: THREE.Mesh[]) => void;
      cleanupAllSelections: () => void;
      checkOverlaps: () => void;
      refs: {
        selectedMeshes: React.RefObject<THREE.Mesh[]>;
        queueLines: React.RefObject<QueueLineModel[]>;
      };
    }
  ): void {
    const currentLineId = shopper.userData.lineId;

    switch (action) {
      case 'updateShopperType':
        if (typeof value === 'number') {
          const originalShopperId = shopper.id;
          this.updateShopperType(scene, shopper, value);

          callbacks.setQueueLines((prevLines) => {
            const newLines = [...prevLines];
            const lineIndex = newLines.findIndex(
              (line) => line.lineId === shopper.userData.lineId
            );
            if (lineIndex >= 0) {
              const updatedShoppers = scene.children.filter(
                (obj): obj is ShopperMesh =>
                  obj instanceof THREE.Mesh &&
                  obj.userData.isQueueLineShopper &&
                  obj.userData.lineId === shopper.userData.lineId
              );
              newLines[lineIndex].shoppers = updatedShoppers;
            }
            return newLines;
          });

          const newShopper = scene.children.find(
            (obj): obj is ShopperMesh =>
              obj instanceof THREE.Mesh &&
              obj.userData.isQueueLineShopper &&
              obj.userData.lineId === currentLineId &&
              obj.id !== originalShopperId
          );

          if (newShopper && newShopper.userData.hasOverlay) {
            callbacks.setSelectedMeshes([newShopper]);
          }
        }
        break;

      case 'removeShopper':
        callbacks.setQueueLines((prevLines) => {
          const newLines = [...prevLines];
          const allDeleted = this.removeShopper(scene, shopper, newLines);

          if (allDeleted) {
            callbacks.cleanupAllSelections();
            const resetLines = this.initializeQueueLines();
            return resetLines;
          }

          const lineIndex = newLines.findIndex(
            (line) => line.lineId === shopper.userData.lineId
          );
          if (lineIndex >= 0) {
            const updatedShoppers = scene.children.filter(
              (obj): obj is ShopperMesh =>
                obj instanceof THREE.Mesh &&
                obj.userData.isQueueLineShopper &&
                obj.userData.lineId === shopper.userData.lineId
            );
            newLines[lineIndex].shoppers = updatedShoppers;
          }

          // Queue lines state is managed through callbacks.setQueueLines
          return newLines;
        });

        if (!this.areAllShoppersDeleted(scene)) {
          const nextShopper = scene.children.find(
            (obj): obj is ShopperMesh =>
              obj instanceof THREE.Mesh &&
              obj.userData.isQueueLineShopper &&
              obj.userData.lineId === currentLineId &&
              obj !== shopper
          );

          if (nextShopper) {
            callbacks.setSelectedMeshes([nextShopper]);
            addSelectionOverlay(
              nextShopper,
              scene,
              SELECTION_COLOR,
              SELECTION_OPACITY
            );
            updateShopperBadgeSelection(nextShopper, true);
          } else {
            callbacks.setSelectedMeshes([]);
          }
        }
        break;

      case 'updateElapsedWaitTime':
        if (typeof value === 'string') {
          this.updateShopperWaitTime(shopper, value);
        }
        break;
    }

    // Check for overlaps after any shopper update
    callbacks.checkOverlaps();
  }
}

// Export singleton instance
export const queueLineManager = new QueueLineManager();
